import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import { ContactUsData } from "./ContactUsData";
import React from "react";
import { useLocation } from "react-router-dom";
import { useContactEnquiry } from "../../hooks/useContactEnquiry";

export default function ContactPage() {
  const { submitContactEnquiry, isLoading } = useContactEnquiry();
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace("#", "");
      const element: any = document.getElementById(sectionId);
      const headerOffset = 120; // pixels you want to keep above
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;
      if (element) {
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [location]);

  type ContactFormData = {
    name?: string;
    email?: string;
    mobile?: string;
    message?: string;
  };

  type ContactFormErrors = Partial<Record<keyof ContactFormData, string>>;

  const [formData, setFormData] = React.useState<ContactFormData>({});
  const [errors, setErrors] = React.useState<ContactFormErrors>({});
  const [submitted, setSubmitted] = React.useState(false);
  const nameRegex = /^[A-Za-z ]*$/;
const mobileRegex = /^[6-9]\d{9}$/;

  const validate = (): ContactFormErrors => {
    const newErrors: ContactFormErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!formData.mobile?.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else {
      const cleanedMobile = formData.mobile
        .replace(/\s+/g, "") // remove spaces
        .replace(/^(\+91|0)/, ""); // remove +91 or leading 0

      if (!/^[6-9]\d{9}$/.test(cleanedMobile)) {
        newErrors.mobile = "Enter a valid 10-digit mobile number";
      }
    }

    if (!formData.message?.trim()) {
      newErrors.message = "Message is required";
    }

    return newErrors;
  };
const handleChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
) => {
  const { name, value } = e.target;

  // ---------- NAME ----------
  if (name === "name") {
    // Allow only alphabets and spaces
    if (!nameRegex.test(value)) return;

    setFormData((prev) => ({ ...prev, name: value }));

    setErrors((prev) => {
      const updated = { ...prev };

      if (!value.trim()) {
        updated.name = "Full name is required";
      } else if (value.trim().length < 2) {
        updated.name = "Name must be at least 2 characters";
      } else {
        delete updated.name;
      }

      return updated;
    });

    return;
  }

  /* ================= EMAIL ================= */
  if (name === "email") {
    const emailValue = value.replace(/\s/g, "");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    setFormData((prev) => ({ ...prev, email: emailValue }));

    setErrors((prev) => {
      const updated = { ...prev };

      if (emailValue.length === 0) {
        updated.email = "Email is required";
      } else if (!emailRegex.test(emailValue)) {
        updated.email = "Enter a valid email address";
      } else {
        delete updated.email;
      }

      return updated;
    });

    return;
  }

  // ---------- OTHER FIELDS ----------
  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));

  setErrors((prev) => {
    const updated = { ...prev };
    if (!value) updated[name as keyof ContactFormData] = "This field is required";
    else delete updated[name as keyof ContactFormData];
    return updated;
  });
};

const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");

  setFormData((prev) => ({
    ...prev,
    mobile: onlyNumbers,
  }));

  setErrors((prev) => {
    const updated = { ...prev };

    if (!onlyNumbers) {
      updated.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{0,9}$/.test(onlyNumbers)) {
      updated.mobile = "Number must start with 6–9";
    } else if (onlyNumbers.length < 10) {
      updated.mobile = "Enter 10-digit mobile number";
    } else if (!mobileRegex.test(onlyNumbers)) {
      updated.mobile = "Enter a valid mobile number";
    } else {
      delete updated.mobile;
    }

    return updated;
  });
};



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // ✅ API / console submission
    console.log("Contact Form Submitted:", formData);
    const result = await submitContactEnquiry(formData);
    console.log(result);

    // if (result.success) {
    //   setSubmitStatus({ success: true, message: 'Thank you! We will contact you soon.' });
    //   setFormData({ name: '', email: '', mobile: '', message: '' });
    // } else {
    //   setSubmitStatus({ success: false, message: 'Failed to submit. Please try again.' });
    // }
    // ✅ Show success message
    setSubmitted(true);
  };

  return (
    <>
      <section
        className="relative h-[200px] md:h-[300px] flex items-center justify-center bg-cover bg-center mt-[80px]"
        style={{
          backgroundImage: `url(${ContactUsData.heroData.imgUrl})`,
        }}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/60 via-gray-900/90 to-gray-900"></div>
        <div className="relative z-10 text-center px-4 max-w-3xl">
          <h1 className="text-white font-extrabold text-3xl uppercase md:text-4xl lg:text-5xl tracking mb-2">
            {ContactUsData.heroData.title}
            <span className="text-[#ff1a1a]">
              {ContactUsData.heroData.title2}
            </span>
          </h1>
          <div className="w-20 h-1 bg-[#ff1a1a] mx-auto rounded-full mb-4"></div>
          <p className="text-white text-base md:text-lg font-workSans font-medium">
            {ContactUsData.heroData.subTitle}
          </p>
        </div>
      </section>
      <section className="w-full max-w-7xl mx-auto py-20 px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* ================= CONTACT DETAILS CARD ================= */}
          <div className="bg-white rounded-2xl shadow-lg border p-10 space-y-8">
            <h2 className="text-3xl font-bold text-gray-900">
              Contact Information
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Reach out to ProRes Scientific for sales inquiries, technical
              support, and custom glovebox solutions.
            </p>

            <div className="space-y-6">
              {/* Address 1 */}
              <div className="flex gap-4">
                <FaMapMarkerAlt className="text-red-600 text-xl mt-1" />
                <div>
                  <p className="font-semibold text-gray-800">Head Office</p>
                  <p className="text-gray-600">
                    {ContactUsData.formData.leftSection.address}
                  </p>
                </div>
              </div>

              {/* Address 2 */}
              <div className="flex gap-4">
                <FaMapMarkerAlt className="text-red-600 text-xl mt-1" />
                <div>
                  <p className="font-semibold text-gray-800">
                    Manufacturing Unit
                  </p>
                  <p className="text-gray-600">
                    {ContactUsData.formData.leftSection.address}
                  </p>
                </div>
              </div>

              {/* Phone 1 */}
              <div className="flex gap-4">
                <FaPhoneAlt className="text-red-600 text-xl mt-1" />
                <div>
                  <p className="font-semibold text-gray-800">Phone</p>
                  <p className="text-gray-600">
                    {ContactUsData.formData.leftSection.mobileNUmber}
                  </p>
                </div>
              </div>

              {/* Phone 2 */}
              <div className="flex gap-4">
                <FaPhoneAlt className="text-red-600 text-xl mt-1" />
                <div>
                  <p className="font-semibold text-gray-800">Alternate Phone</p>
                  <p className="text-gray-600">
                    {ContactUsData.formData.leftSection.mobileNumber2}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4">
                <FaEnvelope className="text-red-600 text-xl mt-1" />
                <div>
                  <p className="font-semibold text-gray-800">Email</p>
                  <p className="text-gray-600">
                    {ContactUsData.formData.leftSection.emailAddress}
                  </p>
                  <p className="text-gray-600">
                    {ContactUsData.formData.leftSection.emailAddress2}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ================= MAP + FORM CARD ================= */}
          <div className="bg-white rounded-2xl shadow-lg border overflow-hidden" id="contact-form">
            {/* Contact Form */}
            <div className="p-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>

              {submitted ? (
                <div className=" p-6 rounded-md text-green-700">
                  <h3 className="text-xl font-semibold mb-2">
                    Thank you for contacting us!
                  </h3>
                  <p>Our team will get back to you shortly.</p>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleSubmit}>
                  {/* Name */}
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name || ""}
                      onChange={handleChange}
                      placeholder="Name"
                      className="peer w-full border-b-2 border-gray-300 py-3 outline-none
               focus:border-red-600 placeholder-transparent"
                    />

                    <label
                    htmlFor="name"
                      className="absolute left-0 top-3 text-gray-500 text-sm transition-all
               peer-placeholder-shown:top-3 peer-placeholder-shown:text-base
               peer-placeholder-shown:text-gray-400
               peer-focus:-top-2 peer-focus:text-sm peer-focus:text-red-600
               peer-[&:not(:placeholder-shown)]:-top-2
               peer-[&:not(:placeholder-shown)]:text-sm"
                    >
                      Name <span className="text-red-600">*</span>
                    </label>

                    {errors.name && (
                      <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      placeholder="Email"
                      className="peer w-full border-b-2 border-gray-300 py-3 outline-none
               focus:border-red-600 placeholder-transparent"
                    />

                    <label
                    htmlFor="email"

                      className="absolute left-0 top-3 text-gray-500 text-sm transition-all
               peer-placeholder-shown:top-3 peer-placeholder-shown:text-base
               peer-placeholder-shown:text-gray-400
               peer-focus:-top-2 peer-focus:text-sm peer-focus:text-red-600
               peer-[&:not(:placeholder-shown)]:-top-2
               peer-[&:not(:placeholder-shown)]:text-sm"
                    >
                      Email <span className="text-red-600">*</span>
                    </label>

                    {errors.email && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Mobile */}
<div className="relative">
  <input
    type="tel"
    name="mobile"
    id="mobile"
    value={formData.mobile || ""}
    onChange={handleMobileChange}
    inputMode="numeric"
    maxLength={10}
    placeholder="Mobile"
    className="peer w-full border-b-2 border-gray-300 py-3 outline-none
               focus:border-red-600 placeholder-transparent"
  />

  <label
  htmlFor="mobile"
    className="absolute left-0 top-3 text-gray-500 text-sm transition-all
               peer-placeholder-shown:top-3 peer-placeholder-shown:text-base
               peer-placeholder-shown:text-gray-400
               peer-focus:-top-2 peer-focus:text-sm peer-focus:text-red-600
               peer-[&:not(:placeholder-shown)]:-top-2
               peer-[&:not(:placeholder-shown)]:text-sm"
  >
    Mobile <span className="text-red-600">*</span>
  </label>

  {errors.mobile && (
    <p className="text-sm text-red-600 mt-1">{errors.mobile}</p>
  )}
</div>



                  {/* Message */}
                  <div className="relative">
                    <textarea
                      rows={4}
                      name="message"
                      id="message"
                      value={formData.message || ""}
                      onChange={handleChange}
                      placeholder="Message"
                      className="peer w-full border-b-2 border-gray-300 py-3 outline-none
               focus:border-red-600 resize-none placeholder-transparent"
                    />

                    <label
                    htmlFor="message"
                      className="absolute left-0 top-3 text-gray-500 text-sm transition-all
               peer-placeholder-shown:top-3 peer-placeholder-shown:text-base
               peer-placeholder-shown:text-gray-400
               peer-focus:-top-2 peer-focus:text-sm peer-focus:text-red-600
               peer-[&:not(:placeholder-shown)]:-top-2
               peer-[&:not(:placeholder-shown)]:text-sm"
                    >
                      Message <span className="text-red-600">*</span>
                    </label>

                    {errors.message && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    className="bg-red-600 hover:bg-red-700 text-white px-10 py-4 rounded-md 
                 font-semibold transition-all"
                  >
                    Submit Enquiry
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
        {/* Google Map */}
        <div className="w-full h-[280px] mt-[20px]">
          <iframe
            title="Google Map"
            src="https://www.google.com/maps?q=ProRes%20Scientific%20Bangalore&output=embed"
            className="w-full h-full border-0"
            loading="lazy"
          ></iframe>
        </div>
      </section>
    </>
  );
}
